import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Tab,
  TableBody,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  OutputOutlined as ExportarIcon,
  ErrorOutline as SinTemaIcon,
  FactCheckOutlined as ExpedienteIcon,
  MailOutline as CorreoIcon,
  SyncOutlined as SyncIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ExpedienteDialog from "../../components/app/ExpedienteDialog";
import AvatarAmpliable from "../../components/kit/AvatarAmpliable";
import PoblarPasoDialog from "../../components/app/PoblarPasoDialog";
import { CeldaKit, CeldaPrincipal, Columna, FilaKit, TablaKit } from "../../components/kit/TablaKit";
import EstadoChip from "../../components/kit/EstadoChip";
import EnlaceTema from "../../components/tema/EnlaceTema";
import { Tarjeta } from "../../components/kit/Tarjeta";
import { useEstudiantes } from "../../queries/useEstudiantes";
import { useNotificarEstudiantes } from "../../queries/useCorreos";
import { usePasos } from "../../queries/usePasos";
import { estudianteService } from "../../services/estudianteService";
import { useAuthStore } from "../../stores/authStore";
import { tokens } from "../../theme";
import { EstudianteResumen, UltimaInstancia } from "../../types/estudiante";
import { ResultadoNotificacion } from "../../types/evaluador";
import { Paso } from "../../types/paso";
import { mensajeDeError } from "../../utils/errores";
import { estiloDeInstanciaTexto } from "../../utils/estados";
import { formatFechaHora } from "../../utils/fecha";
import { ordenarPasosPorFlujo } from "../../utils/flujoPasos";
import { useFiltrosUrl, usePestanaUrl } from "../../utils/estadoLista";
import { Accesores, ordenar, useOrdenUrl } from "../../utils/orden";
import { coincide } from "../../utils/texto";
import { etiquetaPeriodo } from "../../utils/semestre";
import { usePeriodosProcesos } from "../../queries/useInstancias";

const COLUMNAS: (string | Columna)[] = [
  { label: "Estudiante", sortKey: "nombre" },
  { label: "RUT", sortKey: "rut" },
  { label: "Estado", sortKey: "estado" },
  { label: "Comisión", sortKey: "comision" },
  { label: "Periodo", sortKey: "periodo", width: 96 },
];

const ACC_EST: Accesores<EstudianteResumen> = {
  nombre: (e) => e.nombre,
  rut: (e) => e.rut,
  estado: (e) => e.ultima_instancia?.estado,
  // Avance de la comisión como fracción, no como cuenta: ordenar por cuántos
  // respondieron dejaba un 1/1 ya completo debajo de un 2/4 a medio camino, porque
  // 1 < 2. Dividido por el tamaño, 1/1 y 4/4 valen lo mismo —listos— y la columna
  // ordena por lo que el chip dice.
  //
  // Sin comisión va por debajo del 0: "no tiene a quién esperar" no es lo mismo que
  // "tiene comisión y todavía no responde nadie". Así, en descendente, todas las
  // comisiones formadas quedan juntas arriba —incluidas las que van en 0/4— y las que
  // ni siquiera se han armado caen al final.
  comision: (e) => {
    const ui = e.ultima_instancia;
    if (!ui || ui.comision_total === 0) return -1;
    return ui.comision_evaluaron / ui.comision_total;
  },
  periodo: (e) => e.ultima_instancia?.periodo,
};

/**
 * El tema del estudiante, bajo su nombre. Sin tema es información, no un hueco:
 * va marcado, porque conseguirlo es el primer trámite del proceso y es lo que
 * hay que ir a resolver.
 */
function TemaDelEstudiante({ ui }: { ui: UltimaInstancia | null }) {
  if (ui?.tema) {
    return (
      <Typography
        variant="caption"
        title={ui.tema}
        sx={{
          display: "block",
          color: "text.secondary",
          fontWeight: 400,
          lineHeight: 1.35,
          mt: 0.25,
          // Un título largo no puede ensanchar la columna: se corta y queda entero
          // en el tooltip.
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        <EnlaceTema id={ui.tema_id} titulo={ui.tema} />
      </Typography>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={0.4} sx={{ mt: 0.25, color: tokens.chip.aviso.fg }}>
      <SinTemaIcon sx={{ fontSize: 14 }} />
      <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
        Sin tema
      </Typography>
    </Stack>
  );
}

/** Cuántos de ese paso no tienen tema. En la pestaña, para verlo sin entrar. */
function ContadorSinTema({ n }: { n: number }) {
  if (!n) return null;
  return (
    <Box
      component="span"
      title={`${n} sin tema`}
      sx={{
        ml: 0.75,
        px: 0.75,
        minWidth: 20,
        borderRadius: "500px",
        bgcolor: tokens.chip.aviso.bg,
        color: tokens.chip.aviso.fg,
        fontSize: 11,
        fontWeight: 700,
        lineHeight: "18px",
        display: "inline-block",
        textAlign: "center",
      }}
    >
      {n}
    </Box>
  );
}

/** Avance de la comisión evaluadora: "evaluaron/total"; verde con ✓ si ya evaluaron todos. */
function ComisionAvance({ ui }: { ui: UltimaInstancia | null }) {
  if (!ui || ui.comision_total === 0) return <>—</>;
  const completa = ui.comision_evaluaron >= ui.comision_total;
  return (
    <EstadoChip label={`${ui.comision_evaluaron}/${ui.comision_total}`} tono={completa ? "exito" : "aviso"} icono={completa ? "check" : undefined} denso />
  );
}

// "Pendiente" para esta vista = proceso no finalizado (aún requiere acción). Entran
// los dos estados abiertos que no son "en evaluación": el que entregó y todavía no
// tiene comisión, y el que quedó en observación esperando que alguien lo resuelva.
const NO_FINALIZADO = ["pendiente", "entregado", "en_evaluacion", "observacion"];

// Cerrar un paso en el semestre vigente no es desaparecer de él: es lo último que le
// pasó a esa persona, y es ahí donde el coordinador la busca. Vale igual para el que
// reprobó —que además es a quien hay que ir a ver— que para el que aprobó.
const CERRADOS = ["aprobado", "reprobado"];

/**
 * Quién va en las pestañas por paso: lo que sigue abierto, y también quien cerró ese
 * paso en el semestre vigente y todavía no tiene el siguiente. Es el mismo criterio
 * que "Mis estudiantes" en Mis Procesos (`utils/agruparStaff.ts`).
 *
 * "Sin paso siguiente" no hay que comprobarlo: `ultima_instancia` ya es el último
 * proceso del estudiante, así que si tuviera el siguiente, la última sería esa.
 */
function enCurso(ui: UltimaInstancia | null, vigente: string): boolean {
  if (!ui) return false;
  if (NO_FINALIZADO.includes(ui.estado_codigo)) return true;
  return CERRADOS.includes(ui.estado_codigo) && !!vigente && ui.periodo === vigente;
}

/**
 * "Estudiantes": para docentes/evaluadores muestra los que guían o evalúan; el
 * coordinador ve a todos. Por defecto agrupa en tabs por el último paso a los
 * estudiantes con proceso pendiente; un botón revela la lista completa. El
 * backend gatea el acceso (lista vacía si no corresponde).
 */
export default function Estudiantes() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [verTodos, setVerTodos] = useState(false);
  // La pestaña (?paso=), la búsqueda, el período y el orden viven en la URL: volver
  // de la ficha de un estudiante deja la lista exactamente como estaba.
  const { leer, filtrar } = useFiltrosUrl();
  const q = leer("q");
  const setQ = (v: string) => filtrar({ q: v });
  const periodo = leer("periodo");
  const setPeriodo = (v: string) => filtrar({ periodo: v });
  const { data, isLoading, isError } = useEstudiantes(periodo);
  // Los semestres del selector no salen de las filas descargadas: con un semestre
  // elegido, la lista trae solo ese y el selector se quedaría con una sola opción.
  const { data: periodosProcesos } = usePeriodosProcesos();

  const todos = useMemo(() => data ?? [], [data]);
  const periodos = useMemo(
    () => (periodosProcesos ?? []).map((p) => `${p.ano}.${p.semestre}`),
    [periodosProcesos],
  );

  // Búsqueda por nombre/RUT. El período ya lo filtró el servidor, que además eligió
  // qué proceso mostrar de cada uno. El nombre se busca con `coincide`: sin tildes,
  // sin importar mayúsculas y por palabras sueltas, así "nunez carla" encuentra a
  // "Carla Elena Núñez Pardo".
  const estudiantes = useMemo(() => {
    const term = q.trim();
    return todos.filter((e) => !term || coincide(`${e.nombre} ${e.rut}`, term));
  }, [todos, q]);

  // El semestre vigente es el más reciente con procesos: el mismo criterio que
  // `cohorte_vigente()` en el backend, leído de los datos y no del calendario.
  // Sale de `periodos`, que es de la lista completa y no de la filtrada.
  const vigente = periodos[0] ?? "";
  // Sin semestre elegido se muestra lo que está en curso. **Con semestre elegido se
  // muestra todo el que estuvo ahí**, cerrado o no: elegir un semestre es ir a mirar
  // esa cohorte, y filtrarla además por estado dejaba fuera justamente a los que ya
  // terminaron, que son casi todos cuando el semestre pasó.
  const pendientes = useMemo(
    () => (periodo ? estudiantes : estudiantes.filter((e) => enCurso(e.ultima_instancia, vigente))),
    [estudiantes, periodo, vigente],
  );

  // Catálogo de pasos (solo el coordinador; vacío para docentes/evaluadores) en orden de flujo.
  const { data: pasosData } = usePasos(!!user?.es_coordinador_global);
  const pasosFlujo = useMemo(() => ordenarPasosPorFlujo(pasosData ?? []), [pasosData]);

  // Agrupa los pendientes por su último paso (por nombre).
  const porPaso = useMemo(() => {
    const m = new Map<string, EstudianteResumen[]>();
    for (const e of pendientes) {
      const paso = e.ultima_instancia!.paso;
      if (!m.has(paso)) m.set(paso, []);
      m.get(paso)!.push(e);
    }
    return m;
  }, [pendientes]);

  // Pestañas. Con el catálogo completo (coordinador): SIEMPRE una por paso —aunque
  // esté vacía— en orden de flujo (prerrequisitos primero). Sin catálogo (docente):
  // solo los pasos con estudiantes, ordenados por cantidad. Cada una lleva su
  // contador de gente sin tema, que es lo que antes era una pestaña aparte.
  const tabsData = useMemo(() => {
    // `catalogo` solo viene con el catálogo de pasos (coordinador): es lo que
    // habilita el botón de actualizar, que necesita el id y la regla del paso.
    const arma = (paso: string, ests: EstudianteResumen[], catalogo?: Paso) => ({
      key: paso,
      paso,
      ests,
      catalogo,
      sinTema: ests.filter((e) => !e.ultima_instancia?.tema).length,
    });
    return pasosFlujo.length
      ? pasosFlujo.map((p) => arma(p.nombre, porPaso.get(p.nombre) ?? [], p))
      : [...porPaso.entries()]
          .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
          .map(([paso, ests]) => arma(paso, ests));
  }, [pasosFlujo, porPaso]);

  // Índice de la pestaña activa según su `key` en la URL (0 si no hay o no calza).
  // Por clave y no por índice: las pestañas son los pasos que hoy tienen gente, y su
  // posición cambia sola cuando un paso se vacía.
  const [pasoTab, setPasoTab] = usePestanaUrl("paso", tabsData.map((t) => t.key));
  const tabActual = Math.max(0, tabsData.findIndex((t) => t.key === pasoTab));
  const seleccionarTab = (i: number) => setPasoTab(tabsData[i].key);
  const abrir = (rut: number) => navigate(`/app/estudiantes/${rut}`);

  // Selección para mandar correo (igual que en Evaluadores). Solo el coordinador:
  // el endpoint es suyo y es quien ve la lista completa.
  const puedeEscribir = !!user?.es_coordinador_global;
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [correo, setCorreo] = useState(false);
  // Lo que se está mirando: es sobre esto que operan la selección y el correo.
  const visibles = verTodos ? estudiantes : (tabsData[tabActual]?.ests ?? []);
  // Cambiar de filtro o de pestaña limpia la selección: si no, quedarían marcados
  // estudiantes que ya no están a la vista y el correo saldría a gente que no se ve.
  const claveVista = `${verTodos}|${tabActual}|${q}|${periodo}`;
  useEffect(() => setSel(new Set()), [claveVista]);

  const alternar = (rut: number) =>
    setSel((prev) => {
      const s = new Set(prev);
      s.has(rut) ? s.delete(rut) : s.add(rut);
      return s;
    });
  const elegidos = visibles.filter((e) => sel.has(e.rut));
  // Por persona y no por fila: con un semestre elegido la misma puede venir en varias
  // (una por proceso), y marcarla una vez la marca entera.
  const rutsVisibles = useMemo(() => new Set(visibles.map((e) => e.rut)), [visibles]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Estudiantes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {verTodos
          ? "Todos los estudiantes que guías o evalúas, con el estado de su último proceso."
          : periodo
            ? `Todos los que estuvieron en ${etiquetaPeriodo(periodo)}, en cualquier estado, agrupados por su paso.`
            : "Estudiantes con un proceso pendiente o cerrado este semestre, agrupados por su último paso."}
      </Typography>

      {!isLoading && !isError && todos.length > 0 && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
          <TextField
            size="small"
            label="Buscar"
            placeholder="Nombre, apellido o RUT…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <TextField select size="small" label="Período" value={periodo} onChange={(e) => setPeriodo(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">Todos</MenuItem>
            {periodos.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      )}

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && <Alert severity="error">No se pudieron cargar los estudiantes.</Alert>}

      {!isLoading && !isError && estudiantes.length === 0 && <Vacio hint="No hay estudiantes para mostrar." />}

      {!isLoading && !isError && estudiantes.length > 0 && (
        <>
          {verTodos && puedeEscribir && visibles.length > 0 && (
            <BarraSeleccion
              visibles={visibles}
              sel={sel}
              onTodos={() => setSel(sel.size === rutsVisibles.size ? new Set() : new Set(rutsVisibles))}
              onCorreo={() => setCorreo(true)}
            />
          )}

          {verTodos ? (
            <EstudiantesTabla
              estudiantes={estudiantes}
              onSelect={abrir}
              sel={puedeEscribir ? sel : undefined}
              onAlternar={puedeEscribir ? alternar : undefined}
            />
          ) : tabsData.length === 0 ? (
            <Vacio hint="No hay estudiantes con un proceso pendiente." />
          ) : (
            <>
              <Tabs
                value={tabActual}
                onChange={(_, v) => seleccionarTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
              >
                {tabsData.map((t) => (
                  <Tab
                    key={t.key}
                    // Un solo hijo: `Tab` apila en columna lo que le llegue suelto,
                    // y el contador terminaría bajo el nombre del paso.
                    label={
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                        {t.paso} ({t.ests.length})
                        <ContadorSinTema n={t.sinTema} />
                      </Box>
                    }
                    sx={{ textTransform: "none" }}
                  />
                ))}
              </Tabs>
              <AccionesDelPaso paso={tabsData[tabActual].catalogo} nombre={tabsData[tabActual].paso} estudiantes={tabsData[tabActual].ests} periodo={periodo} />
              {puedeEscribir && visibles.length > 0 && (
                <BarraSeleccion
                  visibles={visibles}
                  sel={sel}
                  onTodos={() => setSel(sel.size === rutsVisibles.size ? new Set() : new Set(rutsVisibles))}
                  onCorreo={() => setCorreo(true)}
                />
              )}
              <EstudiantesTabla
                estudiantes={tabsData[tabActual].ests}
                onSelect={abrir}
                sel={puedeEscribir ? sel : undefined}
                onAlternar={puedeEscribir ? alternar : undefined}
              />
            </>
          )}

          <Stack alignItems="center" sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => setVerTodos((v) => !v)}>
              {verTodos ? "Ver solo pendientes" : "Ver todos los estudiantes"}
            </Button>
          </Stack>

          {correo && (
            <CorreoEstudiantesDialog
              estudiantes={elegidos}
              onClose={() => setCorreo(false)}
              onEnviado={() => setSel(new Set())}
            />
          )}
        </>
      )}
    </Box>
  );
}

/**
 * Barra de acciones del paso de la pestaña, sobre la lista que cambian.
 *
 * Vivían en "Pasos y evaluación", que es donde se configura el paso pero no donde
 * se ve el resultado: había que actuar en una pantalla e irse a otra a comprobar.
 *
 * Son dos cosas distintas: **actualizar** trae al paso a quien le corresponde
 * (escribe en el sistema), **revisar expediente** va a leer a UCampus cómo va el
 * trámite de titulación de los que ya están (no escribe hasta que se guarde).
 * Solo el coordinador las ve: es el único que recibe el catálogo de pasos.
 */
function AccionesDelPaso({
  paso,
  nombre,
  estudiantes,
  periodo,
}: {
  paso?: Paso;
  nombre: string;
  estudiantes: EstudianteResumen[];
  periodo: string;
}) {
  const [poblar, setPoblar] = useState(false);
  const [expediente, setExpediente] = useState(false);
  if (!paso) return null;

  // Los pasos manuales (Vía Rápida) no se pueblan solos: se llenan moviendo gente
  // desde "Pasos y evaluación". Su expediente sí se puede revisar.
  const sePuebla = paso.regla_ingreso !== "manual";
  const poblados = paso.poblados ?? [];
  const ultimo = periodo ? poblados.find((p) => p.periodo === periodo) : poblados[0];
  const deUCampus = paso.regla_ingreso === "inscripcion_curso";
  const [ano, semestre] = periodo.split(".").map(Number);

  return (
    // Los botones a la izquierda y la fecha pegada al suyo: describe cuándo se
    // pobló el paso, no el expediente, y con dos botones en la barra puesta al
    // otro extremo se leía como si hablara de los dos.
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems={{ sm: "center" }}
      sx={{ mb: 1.5 }}
      flexWrap="wrap"
      useFlexGap
    >
      {estudiantes.length > 0 && (
        <Button size="small" variant="outlined" startIcon={<ExpedienteIcon />} onClick={() => setExpediente(true)}>
          Revisar expediente
        </Button>
      )}

      {sePuebla && (
        <>
          <Button size="small" variant="outlined" startIcon={<SyncIcon />} onClick={() => setPoblar(true)}>
            {deUCampus ? "Actualizar desde UCampus" : "Actualizar desde el paso anterior"}
          </Button>
          {/* La fecha es la del semestre que se esté mirando: con el filtro de período
              puesto, la de ese período —y "sin actualizar" si nunca se corrió ahí—;
              sin filtro, la de la última corrida, sea del semestre que sea. */}
          <Typography variant="caption" color={ultimo ? "text.secondary" : "text.disabled"}>
            {ultimo
              ? `Actualizado el ${formatFechaHora(ultimo.fecha)} · ${etiquetaPeriodo(ultimo.periodo)}`
              : periodo
                ? `Sin actualizar en ${etiquetaPeriodo(periodo)}`
                : "Nunca se ha actualizado"}
            {ultimo?.por && ` · ${ultimo.por}`}
          </Typography>
        </>
      )}

      {poblar && (
        <PoblarPasoDialog
          paso={paso}
          ano={periodo ? ano : undefined}
          semestre={periodo ? semestre : undefined}
          onClose={() => setPoblar(false)}
        />
      )}
      {expediente && (
        <ExpedienteDialog
          paso={nombre}
          estudiantes={estudiantes.map((e) => ({ rut: e.rut, nombre: e.nombre }))}
          onClose={() => setExpediente(false)}
        />
      )}
    </Stack>
  );
}

/**
 * Marcar estudiantes para escribirles, sobre la lista que se está mirando.
 *
 * "Seleccionar todos" son los que están a la vista, no todos los del sistema:
 * marcar lo que no se ve es la forma más fácil de mandarle un correo a alguien
 * por accidente.
 */
function BarraSeleccion({
  visibles,
  sel,
  onTodos,
  onCorreo,
}: {
  visibles: EstudianteResumen[];
  sel: Set<number>;
  onTodos: () => void;
  onCorreo: () => void;
}) {
  const marcados = visibles.filter((e) => sel.has(e.rut)).length;
  const [exportando, setExportando] = useState(false);
  // Lo que se ve, y si hay marcados, solo eso: el Excel es de esta tabla, no de toda la
  // historia. Va por proceso, así que con un semestre elegido salen todas sus filas.
  const aExportar = (marcados ? visibles.filter((e) => sel.has(e.rut)) : visibles)
    .map((e) => e.ultima_instancia?.id)
    .filter((id): id is number => id != null);
  const exportar = async () => {
    setExportando(true);
    try {
      await estudianteService.exportarExcel(aExportar);
    } finally {
      setExportando(false);
    }
  };
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
      <FormControlLabel
        sx={{ mr: 0.5 }}
        control={
          <Checkbox
            size="small"
            checked={marcados > 0 && marcados === visibles.length}
            indeterminate={marcados > 0 && marcados < visibles.length}
            onChange={onTodos}
          />
        }
        label={<Typography variant="body2">Seleccionar todos</Typography>}
      />
      <Button size="small" variant="outlined" startIcon={<CorreoIcon />} disabled={marcados === 0} onClick={onCorreo}>
        Enviar correo{marcados > 0 ? ` (${marcados})` : ""}
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ExportarIcon />}
        disabled={exportando || aExportar.length === 0}
        onClick={exportar}
        sx={{ ml: "auto" }}
      >
        {exportando ? "Exportando…" : `Exportar a Excel (${aExportar.length})`}
      </Button>
    </Stack>
  );
}

/**
 * Correo del coordinador a los estudiantes marcados en la tabla.
 *
 * Sale con el nombre de quien lo escribe y su dirección en el `Reply-To`: para
 * el estudiante es un mensaje de una persona, y al responder le llega a ella. La
 * dirección de envío sigue siendo la del sistema (ver `core.notificaciones`).
 *
 * Los correos se resuelven en el servidor a partir del RUT; el listado no los expone.
 */
function CorreoEstudiantesDialog({
  estudiantes,
  onClose,
  onEnviado,
}: {
  estudiantes: EstudianteResumen[];
  onClose: () => void;
  onEnviado: () => void;
}) {
  const { user } = useAuthStore();
  const notificar = useNotificarEstudiantes();
  const [asunto, setAsunto] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const [resultado, setResultado] = useState<ResultadoNotificacion | null>(null);

  const enviar = () =>
    notificar.mutate(
      { ruts: estudiantes.map((e) => e.rut), asunto: asunto.trim(), cuerpo: cuerpo.trim() },
      { onSuccess: setResultado },
    );

  const firma = [user?.first_name, user?.last_name].filter(Boolean).join(" ");

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enviar correo a {estudiantes.length} estudiante(s)</DialogTitle>
      <DialogContent dividers>
        {resultado ? (
          <Stack spacing={1.5}>
            <Alert severity="success">Se enviaron {resultado.enviados} correo(s).</Alert>
            {resultado.fallidos > 0 && <Alert severity="error">{resultado.fallidos} no se pudieron enviar.</Alert>}
            {resultado.sin_correo.length > 0 && (
              <Alert severity="warning">Sin dirección de correo: {resultado.sin_correo.join(", ")}.</Alert>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Sale a nombre de {firma || "ti"}
              {user?.email ? `, y las respuestas llegan a ${user.email}` : ""}. Los estudiantes sin correo cargado se informan al
              final.
            </Typography>
            {!user?.email && (
              <Alert severity="warning">
                Tu perfil no tiene correo: el mensaje saldrá sin dirección de respuesta. Puedes agregarlo en «Mi perfil».
              </Alert>
            )}
            <TextField label="Asunto" value={asunto} onChange={(e) => setAsunto(e.target.value)} fullWidth required />
            <TextField
              label="Mensaje"
              value={cuerpo}
              onChange={(e) => setCuerpo(e.target.value)}
              fullWidth
              multiline
              minRows={6}
              required
            />
            {notificar.isError && <Alert severity="error">{mensajeDeError(notificar.error, "No se pudo enviar el correo.")}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            if (resultado) onEnviado();
            onClose();
          }}
        >
          {resultado ? "Cerrar" : "Cancelar"}
        </Button>
        {!resultado && (
          <Button variant="contained" disabled={notificar.isPending || !asunto.trim() || !cuerpo.trim()} onClick={enviar}>
            {notificar.isPending ? "Enviando…" : "Enviar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Tabla (desktop) + tarjetas (móvil) de una lista de estudiantes.
 *
 * `sel` llega solo cuando hay a quién escribirle (el coordinador): sin eso la
 * tabla se pinta como siempre, sin columna de casillas.
 */
function EstudiantesTabla({
  estudiantes,
  onSelect,
  sel,
  onAlternar,
}: {
  estudiantes: EstudianteResumen[];
  onSelect: (rut: number) => void;
  sel?: Set<number>;
  onAlternar?: (rut: number) => void;
}) {
  // En la URL: volver de la ficha de alguien deja la tabla ordenada como estaba.
  const { orden, onSort } = useOrdenUrl();
  const conSeleccion = !!sel && !!onAlternar;
  const columnas = conSeleccion ? [{ label: "", width: 44, compacta: true }, ...COLUMNAS] : COLUMNAS;
  return (
    <>
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <TablaKit columnas={columnas} orden={orden} onSort={onSort}>
          <TableBody>
            {ordenar(estudiantes, ACC_EST, orden).map((e) => (
              <FilaKit key={e.rut} destacada={sel?.has(e.rut)} onClick={() => onSelect(e.rut)}>
                {conSeleccion && (
                  // La casilla no debe abrir la ficha del estudiante.
                  <CeldaKit compacta>
                    <Box component="span" onClick={(ev) => ev.stopPropagation()} sx={{ display: "inline-flex" }}>
                      {/* Menos relleno que el de MUI: la fila entera ya es un
                          blanco grande, la casilla no necesita 38 px de columna. */}
                      <Checkbox size="small" sx={{ p: 0.5 }} checked={sel!.has(e.rut)} onChange={() => onAlternar!(e.rut)} />
                    </Box>
                  </CeldaKit>
                )}
                <CeldaPrincipal maxWidth={380}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <AvatarAmpliable src={e.foto} nombre={e.nombre} />
                    <Box sx={{ minWidth: 0 }}>
                      {e.nombre}
                      <TemaDelEstudiante ui={e.ultima_instancia} />
                    </Box>
                  </Stack>
                </CeldaPrincipal>
                <CeldaKit noWrap>{e.rut}</CeldaKit>
                <CeldaKit>
                  {e.ultima_instancia ? (
                    <EstadoChip label={e.ultima_instancia.estado} {...estiloDeInstanciaTexto(e.ultima_instancia.estado)} denso />
                  ) : (
                    "—"
                  )}
                </CeldaKit>
                <CeldaKit>
                  <ComisionAvance ui={e.ultima_instancia} />
                </CeldaKit>
                <CeldaKit noWrap>{e.ultima_instancia?.periodo ?? "—"}</CeldaKit>
              </FilaKit>
            ))}
          </TableBody>
        </TablaKit>
      </Box>

      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <Stack spacing={1.5}>
          {estudiantes.map((e) => (
            <EstudianteCard key={e.rut} estudiante={e} onClick={() => onSelect(e.rut)} />
          ))}
        </Stack>
      </Box>
    </>
  );
}

function EstudianteCard({ estudiante, onClick }: { estudiante: EstudianteResumen; onClick: () => void }) {
  const ui = estudiante.ultima_instancia;
  return (
    <Tarjeta onClick={onClick}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Avatar src={estudiante.foto ?? undefined} sx={{ width: 36, height: 36, fontSize: 15, flexShrink: 0 }}>
            {estudiante.nombre.charAt(0)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, lineHeight: 1.35, color: tokens.tabla.celdaFuerte }}>{estudiante.nombre}</Typography>
            <Typography variant="body2" color="text.secondary">
              {estudiante.rut}
            </Typography>
            <TemaDelEstudiante ui={ui} />
          </Box>
        </Stack>
        {ui && <EstadoChip label={ui.estado} {...estiloDeInstanciaTexto(ui.estado)} denso />}
      </Stack>
      {ui ? (
        <Typography variant="body2" color="text.secondary">
          {ui.paso} · {ui.periodo}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.disabled">
          Sin procesos registrados.
        </Typography>
      )}
    </Tarjeta>
  );
}

function Vacio({ hint }: { hint: string }) {
  return (
    <Paper elevation={0} sx={{ p: 4, textAlign: "center", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Typography color="text.secondary">{hint}</Typography>
    </Paper>
  );
}
